import React, { useEffect, useState } from "react";
import { Form, Input, Radio, Checkbox, Upload, Button } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import {responsesApi, transactionApi} from "../form_formulation/api/index"
import { isEqual } from "lodash";
import { DISABLED_QUESTION, QUESTION_MAPPER } from "./formData";
import secureLocalStorage from "react-secure-storage";
import { toast } from "react-toastify";
import { TOAST_MESSAGE_CSS } from "modules/utilities/utility";
import { useConsultFeedback } from "../dashboards/actionables/store";

const { Item } = Form;

const QuestionComponent = ({ questions, responses, customerData, setShow, formType }) => {
  const [form] = Form.useForm();
  const [initialValues, setInitialValues] = useState({});
  const [dataState, setDataState] = useState({});
  const [loading, setLoading] = useState(false);
  const { gender, caseId } =  customerData;
  const { fetchCaseResponses } = useConsultFeedback();

  useEffect(() => {
    let prepopulatedValues = responses.reduce((acc, item) => {
      try {
        const parsedResponse = JSON.parse(item.response);
        acc[item.question_id] = Array.isArray(parsedResponse)? parsedResponse: parsedResponse || item.response;
      } catch (error) {
        acc[item.question_id] = item.response;
      }
      return acc;
    }, {});
    prepopulatedValues = { ...prepopulatedValues, ...customerData };
    setInitialValues(prepopulatedValues);
    setDataState(prepopulatedValues);
    form.setFieldsValue(prepopulatedValues);
  }, [form, responses, customerData]);

  const handleValuesChange = (_, currentValues) => {
    setDataState((prevState) => ({
      ...prevState,
      ...currentValues,
    }));
  };

  const handleSubmit = async () => {

    const customerDetail = {
      form: [
        {
          question_id: "cohort",
          question: "cohort",
          response: "1",
          form_status: "In-Progress",
        },
      ],
      user: {
        email: dataState.email,
        first_name: dataState.first_name,
        phone_number: dataState.phone_number,
      },
    };
  
    try {
      setLoading(true);
      const response = await responsesApi(customerDetail);
      if (response.status === 200) {
        const questionSequence = [...Object.keys(QUESTION_MAPPER[formType])];
        let payload = {
          form_status: "In-Progress",
          locationPath: "erp.traya.health/lead-details/",
          formFillSource: `Coach form fill ${secureLocalStorage.getItem("id")}`,
        };
  
        for (let index = 0; index < questionSequence.length; index++) {
          const item = questionSequence[index];
  
          if (!dataState[item]) {
            continue;
          }
  
          payload = {
            ...payload,
            question_id: item,
            question: QUESTION_MAPPER[formType][item],
            response: dataState[item],
            ...(questionSequence.length === index+2 ? { form_status: "semi-filled" } : questionSequence.length === index+1 ? {form_status: "filled" } : {}),
          };
  
          try {
            await transactionApi(payload, response?.data?.transactionId);
          } catch (error) {
            console.error("Error processing question:", item, error);
          }
        }
  
        // Last question logic
        setLoading(false);  
        setShow(false);
        toast.success("Form data saved successfully", TOAST_MESSAGE_CSS);
        if(caseId){
          fetchCaseResponses(caseId)
        }
      } else {
        console.error("Form submission failed");
        toast.warn("Form data filling failed");
        setLoading(false);
      }
    } catch (error) {
      console.error("Error while submitting form", error);
      toast.error("Form data filling failed");
      setLoading(false);
    }
  };
  
const renderQuestion = (question) => {
  const { id, text, type, optionMap } = question;

  switch (type) {
    case "text":
      return (
        <Item key={id} name={id} label={text} className="mb-4">
          <Input
            placeholder={`Enter your ${text.toLowerCase()}`}
            className="rounded-md border-gray-300 shadow-sm"
            disabled={DISABLED_QUESTION.includes(id)}
          />
        </Item>
      );

    case "tel":
      return (
        <Item key={id} name={id} label={text} className="mb-4">
          <Input
            type="tel"
            placeholder={`Enter your ${text.toLowerCase()}`}
            className="rounded-md border-gray-300 shadow-sm"
            disabled={DISABLED_QUESTION.includes(id)}
          />
        </Item>
      );

    case "single_choice":
      return (
        <Item key={id} name={id} label={text} className="mb-4">
          <Radio.Group className="space-x-4" disabled={DISABLED_QUESTION.includes(id)}>
            {optionMap.map((option) => (
              <Radio key={option.value} value={option.value}>
                {option.name}
              </Radio>
            ))}
          </Radio.Group>
        </Item>
      );

      case "multiple_choice":
        return (
          <Item key={id} name={id} label={text} className="mb-4">
            <Checkbox.Group
              className="space-x-4"
              value={form.getFieldValue(id) || []}
              onChange={(checkedValues) => {
                let updatedValues = [...checkedValues];

                if (updatedValues.includes("None")) {
                  updatedValues = ["None"];
                } else {
                  updatedValues = updatedValues.filter((val) => val !== "None");
                }

                form.setFieldsValue({ [id]: updatedValues });
                handleValuesChange(null, { ...form.getFieldsValue(), [id]: updatedValues });
              }}
            >
              {optionMap.map((option) => (
                <Checkbox
                  key={option.value}
                  value={option.value}
                  disabled={
                    form.getFieldValue(id)?.includes("None") && option.value !== "None"
                  }
                >
                  {option.name}
                </Checkbox>
              ))}
            </Checkbox.Group>
          </Item>
        );

    default:
      return null;
  }
};

return (
  <Form
    form={form}
    layout="vertical"
    onValuesChange={handleValuesChange}
    onFinish={handleSubmit}
    className="space-y-6"
  >
    {questions.map((question) => renderQuestion(question))}
    <Item>
      <Button
        type="primary"
        htmlType="submit"
        disabled={isEqual(initialValues, dataState)}
        className="w-full py-3 rounded-md bg-blue-600 text-white hover:bg-blue-700"
      >
        {loading? "Please wait, responses is saving" : "Submit"}
      </Button>
    </Item>
  </Form>
);
};

export default QuestionComponent;
